import { CalendarDays, Check, MapPin, Phone } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { trackEvent } from "@/lib/site";

export type EventLandingConfig = {
  path: string;
  title: string;
  description: string;
  kicker: string;
  date: string;
  headline: string;
  line: string;
  offerLine: string;
  bookingNote: string;
  offers: ReadonlyArray<{
    name: string;
    price: string;
    intro: string;
    menu: ReadonlyArray<{
      group: string;
      dishes: string;
    }>;
  }>;
  menuNote: string;
  details: ReadonlyArray<string>;
  bookingLabel: string;
  bookingUrl: string;
  utmCampaign: string;
  phoneHref: string;
  phoneDisplay: string;
  address: string;
  hero: {
    src: string;
    srcSet: string;
    alt: string;
  };
};

export function EventLandingPage({ event, robots }: { event: EventLandingConfig; robots?: string }) {
  return (
    <main className="event-landing-page" id="main-content" tabIndex={-1}>
      <PageMeta
        title={event.title}
        description={event.description}
        path={event.path}
        image={event.hero.src}
        robots={robots}
      />

      <div className="event-landing-band" aria-hidden="true" />
      <div className="event-landing-shell">
        <a className="event-landing-brand" href="/" aria-label="ANGKOR home">
          <img
            src="/manus-storage/angkor-mark-a-crown-360_0d227cac.png"
            width="76"
            height="76"
            alt="ANGKOR"
          />
        </a>

        <section className="event-landing-hero" aria-labelledby="event-heading">
          <div className="event-landing-copy">
            <p className="event-landing-kicker">{event.kicker}</p>
            <h1 id="event-heading">{event.headline}</h1>
            <p className="event-landing-line">{event.line}</p>
            <p className="event-landing-hero-offer">{event.offerLine}</p>
            <p className="event-landing-booking-note">{event.bookingNote}</p>
          </div>

          <figure className="event-landing-visual">
            <img
              src={event.hero.src}
              srcSet={event.hero.srcSet}
              sizes="(max-width: 760px) 100vw, 50vw"
              alt={event.hero.alt}
            />
          </figure>
        </section>

        <section className="event-landing-offer" aria-label="Jay Chou Night feast choices">
          <div className="event-landing-cards">
            {event.offers.map(offer => (
              <article className="event-landing-card" key={offer.name}>
                <h2>{offer.name}</h2>
                <p className="event-landing-card-price">{offer.price}</p>
                <p className="event-landing-card-intro">{offer.intro}</p>
                <dl className="event-landing-menu">
                  {offer.menu.map(item => (
                    <div className="event-landing-menu-group" key={item.group}>
                      <dt>{item.group}</dt>
                      <dd>{item.dishes}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>

          <p className="event-landing-menu-note">{event.menuNote}</p>

          <ul className="event-landing-details">
            {event.details.map(detail => (
              <li key={detail}>
                <Check aria-hidden="true" />
                <span>{detail}</span>
              </li>
            ))}
          </ul>

          <div className="event-landing-actions">
            <a
              className="event-landing-button event-landing-button-primary"
              href={event.bookingUrl}
              onClick={() =>
                trackEvent("event_booking_click", {
                  campaign: event.utmCampaign,
                  placement: "marvel-page",
                })
              }
            >
              <CalendarDays aria-hidden="true" />
              {event.bookingLabel}
            </a>
            <a
              className="event-landing-button event-landing-button-secondary"
              href={event.phoneHref}
              onClick={() =>
                trackEvent("event_call_click", {
                  campaign: event.utmCampaign,
                  placement: "marvel-page",
                })
              }
            >
              <Phone aria-hidden="true" />
              <span>Call us</span>
              <strong>{event.phoneDisplay}</strong>
            </a>
          </div>
        </section>

        <footer className="event-landing-footer">
          <MapPin aria-hidden="true" />
          <span>{event.address}</span>
        </footer>
      </div>
    </main>
  );
}
